import React, { useState } from 'react';
import { Warning, CheckCircleOutline } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  styled,
  Tooltip,
} from '@mui/material';

interface PhotoFlagsProps {
  flags?: string[];
}

const StyledIconButton = styled(IconButton)({
  padding: 5,
  margin: '0 0.3rem',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

const StyledDialog = styled(Dialog)({
  '& .MuiDialogTitle-root': {
    fontFamily: 'ConnectItalic',
  },
  '& .MuiDialogContent-root': {
    fontFamily: 'Connect',
  },
  '& .MuiButton-root': {
    fontFamily: 'Connect',
  },
});

const formatFlagName = (name?: string): string => {
  if (!name) {
    return '';
  }

  return name
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

export const PhotoFlags = ({ flags }: PhotoFlagsProps): JSX.Element => {
  return (
    <Tooltip
      title="There are flags related to the images in this category"
      arrow
      placement="bottom"
    >
      <StyledIconButton>
        <Warning
          color="error"
          sx={{
            width: 24,
            height: 24,
            transition: 'color 0.2s ease',
            '&:hover': {
              color: '#ff0000',
            },
          }}
        />
      </StyledIconButton>
    </Tooltip>
  );
};

export const ConfirmButtonResolveFlag = ({
  onConfirm,
  flagRule,
}: {
  onConfirm: () => void;
  flagRule?: string;
}): JSX.Element => {
  const [showPopup, setShowPopup] = useState(false);

  return (
    <>
      <Tooltip
        title="Resolve flags related to this category"
        arrow
        placement="bottom"
      >
        <StyledIconButton onClick={() => setShowPopup(true)}>
          <CheckCircleOutline
            color="success"
            sx={{
              width: 24,
              height: 24,
              transition: 'color 0.2s ease',
              '&:hover': {
                color: '#00cc00',
              },
            }}
          />
        </StyledIconButton>
      </Tooltip>

      <StyledDialog
        open={showPopup}
        onClose={() => setShowPopup(false)}
        aria-labelledby="confirm-resolution-dialog"
      >
        <DialogTitle id="confirm-resolution-dialog">
          Confirm Resolution
        </DialogTitle>
        <DialogContent>
          <p style={{ fontFamily: 'Connect' }}>
            Are you sure you want to resolve the{' '}
            <strong style={{ fontFamily: 'ConnectItalic' }}>
              {formatFlagName(flagRule)}
            </strong>{' '}
            flag?
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowPopup(false)}
            color="error"
            variant="text"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowPopup(false);
              onConfirm();
            }}
            color="success"
            variant="contained"
            autoFocus
          >
            Resolve
          </Button>
        </DialogActions>
      </StyledDialog>
    </>
  );
};
